// Firestore error reporter. Data-layer modules call `reportFirestoreError(err)`
// from their catch blocks instead of swallowing silently. The reporter
// rate-limits to one report per (code, 60s) window and dispatches a
// `glacier:firestore-error` CustomEvent on `window`, which the React-side
// listener picks up to surface a toast.
//
// Why a window event + listener instead of a direct toast call: the data
// layers are framework-agnostic JS modules, not React components, so they
// can't use the useToast() hook. The CustomEvent bus is a tiny, zero-dep
// indirection that keeps the data layer decoupled from React.

const RATE_LIMIT_MS = 60_000;
let lastReport = { code: '', ts: 0 };

// The Firebase / Firestore error codes we know how to translate to friendly
// copy. Anything else falls through to the generic message.
function friendlyMessage(code) {
  if (code === 'resource-exhausted' || code === 'quota-exceeded') {
    return "Glacier is over its daily database quota — real-time sync may be paused until the next reset.";
  }
  if (code === 'permission-denied') {
    return 'Database access denied — your room may have been removed or the rules updated.';
  }
  if (code === 'unavailable') {
    return 'Glacier database is temporarily unreachable — reconnecting…';
  }
  if (code === 'cancelled' || code === 'deadline-exceeded') {
    return 'Database request timed out — chat may be lagging.';
  }
  return 'Database error — chat features may be degraded.';
}

/**
 * Report a Firestore error from a silent catch. Safe to call from anywhere
 * in the data layer; deduped + rate-limited so a write storm during a quota
 * outage doesn't spam the user with 100 toasts.
 *
 * @param {unknown} err The error caught from a Firestore call.
 */
export function reportFirestoreError(err) {
  if (!err) return;
  const code = (err && err.code) || (err && err.message) || 'unknown';
  const safeCode = String(code).slice(0, 64);
  const now = Date.now();
  if (lastReport.code === safeCode && now - lastReport.ts < RATE_LIMIT_MS) return;
  lastReport = { code: safeCode, ts: now };
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    try {
      window.dispatchEvent(
        new CustomEvent('glacier:firestore-error', {
          detail: {
            code: safeCode,
            message: friendlyMessage(safeCode),
            raw: (err && err.message) || '',
          },
        }),
      );
    } catch {
      /* CustomEvent unavailable (very old env) — drop silently. */
    }
  }
}

/** Reset the dedupe state. Test-only. */
export function __resetForTests() {
  lastReport = { code: '', ts: 0 };
}

export const __test__ = { friendlyMessage };
